import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Breadcrumb } from "@/components/feature/Breadcrumb";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id);

  useEffect(() => {
    if (!article) {
      document.title = "内容不存在 - 爱妻艺";
      return;
    }
    document.title = `${article.title} - 爱妻艺`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", article.summary);
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-4 text-xl font-bold text-foreground-900">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">该资讯可能已被移除，去看看其他内容吧。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "娱乐资讯", to: "/news" },
          { label: article.category },
        ]}
      />

      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-sm bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
            <i className="ri-bookmark-line"></i>
            {article.category}
          </span>
          <h1 className="mt-4 text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-background-200 pb-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              发布于 {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views} 次阅读
            </span>
          </div>

          <div className="mt-6 h-56 w-full overflow-hidden rounded-lg bg-background-100 md:h-72">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 爱妻艺娱乐资讯`}
              className="h-full w-full object-top"
            />
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {article.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2 border-t border-background-200 pt-5">
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-6">
            <Link
              to="/news"
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
          </div>
        </article>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-links-line text-primary-600"></i>
              相关资讯
            </h2>
            <ul className="mt-3 flex flex-col gap-3">
              {related.map((item) => (
                <li key={item.id}>
                  <Link to={`/news/${item.id}`} className="group flex cursor-pointer gap-3">
                    <span className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-100">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 爱妻艺娱乐资讯`}
                        className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-400">{item.date}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-primary-700 p-5 text-background-100">
            <h3 className="text-base font-bold text-background-50">爱妻艺 · 娱乐资讯</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-200">
              每日更新影视圈动态与深度报道，帮你掌握第一手娱乐资讯。
            </p>
            <Link
              to="/news"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              查看更多资讯
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}